<script>
import EcInput from '../ui/EcInput.vue'
import ModuleCard from './ModuleCard.vue'

export default {
  name: 'ModulesList',
  components: { EcInput, ModuleCard },
  props: {
    modules: { type: Array, required: true },
    organization: { type: Object, required: true },
    refreshingServiceId: { type: [String, Number], default: null },
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: 'all',
      refreshingServiceIdInternal: this.refreshingServiceId,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    statusFilters() {
      return [
        { key: 'all', label: this.t('ELIXIO_CONNECT.ALL') },
        { key: 'active', label: this.t('ELIXIO_CONNECT.ACTIVE') },
        { key: 'inactive', label: this.t('ELIXIO_CONNECT.INACTIVE') },
        { key: 'deactivated', label: this.t('ELIXIO_CONNECT.DEACTIVATED') },
      ]
    },
    allModules() {
      return this.modules ?? []
    },
    topLevelModules() {
      return this.allModules.filter((m) => !m.parent)
    },
    filteredModules() {
      let result = this.topLevelModules

      // Status filter
      if (this.statusFilter !== 'all') {
        result = result.filter((m) => {
          const { isActive, isDeactivated } = this.getEffectiveStatus(m)
          if (this.statusFilter === 'active') return isActive
          if (this.statusFilter === 'inactive') return !isActive && !isDeactivated
          if (this.statusFilter === 'deactivated') return isDeactivated
          return true
        })
      }

      // Text search
      const q = this.searchQuery.toLowerCase().trim()
      if (q) {
        result = result.filter(
          (m) =>
            m.name?.toLowerCase().includes(q) ||
            m.description?.toLowerCase().includes(q) ||
            m.slug?.toLowerCase().includes(q),
        )
      }

      return result
    },
  },
  watch: {
    refreshingServiceId(val) {
      this.refreshingServiceIdInternal = val
    },
  },
  methods: {
    getEffectiveStatus(service) {
      if (!service.isParent) {
        return { isActive: !!service.is_active, isDeactivated: !!service.is_deactivated }
      }
      const children = this.allModules.filter((s) => s.parent === service.slug)
      const isActive = children.some((c) => c.is_active)
      const isDeactivated =
        !isActive && children.length > 0 && children.some((c) => c.is_deactivated)
      return { isActive, isDeactivated }
    },
    handleSubmitted(payload) {
      this.$emit('submitted', payload)
    },
    handleClosed() {
      this.$emit('closed')
    },
    handleSubscribed() {
      this.$emit('subscribed')
    },
    handleChildActivated(payload) {
      this.$emit('childActivated', payload)
    },
    handleChildDeactivated(payload) {
      const parent = this.modules.find((m) => m.slug === payload.parentSlug)
      if (!parent) return

      const allChildren = this.modules.filter((m) => m.parent === payload.parentSlug)
      const allChildrenInactive = allChildren.every((child) => !child.is_active)

      if (allChildrenInactive) {
        this.$emit('childDeactivated', { parentSlug: payload.parentSlug, allChildrenInactive: true })
      }
    },
  },
}
</script>

<template>
  <div class="ec-modules-list">
    <!-- Toolbar: filters + search + counter -->
    <div class="ec-modules-list__toolbar">
      <div class="ec-modules-list__filters">
        <button
          v-for="f in statusFilters"
          :key="f.key"
          class="ec-filter-tag"
          :class="{ 'ec-filter-tag--active': statusFilter === f.key }"
          @click="statusFilter = f.key"
        >
          {{ f.label }}
        </button>
      </div>
      <div class="ec-modules-list__search">
        <EcInput
          v-model="searchQuery"
          :placeholder="t('ELIXIO_CONNECT.SEARCH_SERVICES')"
          icon="search"
        />
      </div>
      <div class="ec-modules-list__counter">
        {{ filteredModules.length }} / {{ topLevelModules.length }}
        {{ t('ELIXIO_CONNECT.SERVICES') }}
      </div>
    </div>

    <!-- Grid -->
    <div class="ec-modules-list__grid">
      <div
        v-for="mod in filteredModules"
        :key="mod.slug || mod.id"
        class="ec-modules-list__item"
      >
        <ModuleCard
          :service="mod"
          :organization="organization"
          :all-services="modules"
          :refreshing-service-id="refreshingServiceIdInternal"
          @submitted="handleSubmitted"
          @closed="handleClosed"
          @subscribed="handleSubscribed"
          @child-activated="handleChildActivated"
          @child-deactivated="handleChildDeactivated"
        />
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="filteredModules.length === 0" class="ec-modules-list__empty">
      <i class="bi bi-search" />
      <p>{{ t('ELIXIO_CONNECT.NO_RESULTS') }}</p>
    </div>
  </div>
</template>
